import { HttpError, getJwt } from '../common';
import type {
    TransactionDetail,
    TransactionFilters,
    TransactionListResponse,
    TransactionReceipt,
} from '@/models/payments/Transaction';

const BASE_URL = import.meta.env.VITE_API_URL;

function buildQuery(filters: TransactionFilters): string {
    const p = new URLSearchParams();
    // The org being VIEWED in the scope selector — scopes the query to that org's transactions (which, for a
    // super-admin, is not their login org).
    if (filters.organizationId) p.set('organizationId', filters.organizationId);
    filters.locationIds?.forEach((id) => p.append('locationIds', id));
    if (filters.startDateTime) p.set('startDateTime', filters.startDateTime);
    if (filters.endDateTime) p.set('endDateTime', filters.endDateTime);
    if (filters.cardIssuer) p.set('cardIssuer', filters.cardIssuer);
    if (filters.transactionType) p.set('transactionType', filters.transactionType);
    if (filters.invoiceNumber) p.set('invoiceNumber', filters.invoiceNumber);
    p.set('page', String(filters.page));
    p.set('pageSize', String(filters.pageSize));
    return p.toString();
}

async function getJson<T>(url: string, locationId: string): Promise<T> {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${getJwt()}`,
            // Org scoping is server-side (JWT claim); the location header is sent for convention/parity.
            'x-location-id': locationId,
        },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new HttpError(response.status, data);
    return data as T;
}

const orgParam = (organizationId?: string): string =>
    organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : '';

export const transactionApi = {
    list: (locationId: string, filters: TransactionFilters): Promise<TransactionListResponse> =>
        getJson<TransactionListResponse>(`${BASE_URL}/payments/transactions?${buildQuery(filters)}`, locationId),

    get: (locationId: string, id: string, organizationId?: string): Promise<TransactionDetail> =>
        getJson<TransactionDetail>(`${BASE_URL}/payments/transactions/${id}${orgParam(organizationId)}`, locationId),

    getReceipt: (locationId: string, id: string, organizationId?: string): Promise<TransactionReceipt> =>
        getJson<TransactionReceipt>(`${BASE_URL}/payments/transactions/${id}/receipt${orgParam(organizationId)}`, locationId),
};
